import { Box, Container, Typography, Grid, Chip, Button, Modal, IconButton } from "@mui/material";
import { keyframes } from "@mui/system";
import { useMemo, useState } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Generate random 4-digit job ID
const generateJobId = () => `BZ${Math.floor(1000 + Math.random() * 9000)}`;

// Generate random date within last 15 days (excluding weekends)
const generateRandomDate = () => {
  const today = new Date();
  let randomDate;
  let attempts = 0;
  
  do {
    const daysAgo = Math.floor(Math.random() * 16); // 0 to 15 days ago
    randomDate = new Date(today);
    randomDate.setDate(today.getDate() - daysAgo);
    attempts++;
  } while ((randomDate.getDay() === 0 || randomDate.getDay() === 6) && attempts < 50);
  
  // If still weekend after attempts, move to Friday
  if (randomDate.getDay() === 0) randomDate.setDate(randomDate.getDate() - 2);
  if (randomDate.getDay() === 6) randomDate.setDate(randomDate.getDate() - 1);
  
  const month = String(randomDate.getMonth() + 1).padStart(2, '0');
  const day = String(randomDate.getDate()).padStart(2, '0');
  const year = randomDate.getFullYear();
  return `${month}/${day}/${year}`;
};

const jobsData = [
  {
    title: "Full Stack Developer",
    location: "Texas, USA",
    level: "Senior-level",
    experience: "4+ Years",
    type: "Full-Time",
    description:
      "We believe that design (and you) will be critical to the company's success. You will work with our founders and our early customers to help define and build our product functionality.",
    skills: ["React JS", "Vue Js", "Python", "MongoDB"],
    icon: "💻",
  },
  {
    title: "Frontend Developer",
    location: "San Francisco, USA",
    level: "Mid-level",
    experience: "2-4 Years",
    type: "Full-Time",
    description:
      "Join our dynamic team to create intuitive and visually stunning web applications. You'll collaborate with designers and backend developers to deliver seamless user experiences.",
    skills: ["React JS", "TypeScript", "HTML5", "CSS3"],
    icon: "🎨",
  },
  {
    title: "Backend Engineer",
    location: "Remote",
    level: "Junior-level",
    experience: "1-2 Years",
    type: "Part-Time",
    description:
      "We are looking for a backend engineer to develop and maintain robust APIs. You'll play a critical role in ensuring the reliability and scalability of our server-side systems.",
    skills: ["Node.js", "Express", "MongoDB", "PostgreSQL"],
    icon: "⚙️",
  },
  {
    title: "Data Scientist",
    location: "New York, USA",
    level: "Senior-level",
    experience: "5+ Years",
    type: "Full-Time",
    description:
      "We are seeking a data scientist to derive actionable insights from large datasets. You'll work closely with cross-functional teams to influence business decisions with data-driven strategies.",
    skills: ["Python", "TensorFlow", "SQL", "Big Data"],
    icon: "📊",
  },
  {
    title: "DevOps Engineer",
    location: "Dallas, TX",
    level: "Mid-level",
    experience: "3-5 Years",
    type: "Full-Time",
    description:
      "As a DevOps Engineer, you'll automate and streamline our operations and processes. You'll help maintain development tools and deploy systems in a reliable and scalable manner.",
    skills: ["Docker", "Kubernetes", "AWS", "CI/CD"],
    icon: "🚀",
  },
  {
    title: "Mobile App Developer",
    location: "Seattle, USA",
    level: "Mid-level",
    experience: "3+ Years",
    type: "Full-Time",
    description:
      "We are looking for a Mobile App Developer to build innovative and scalable mobile applications for our global user base.",
    skills: ["Flutter", "React Native", "iOS", "Android"],
    icon: "📱",
  },
  {
    title: "UI/UX Designer",
    location: "Remote",
    level: "Entry-level",
    experience: "0-2 Years",
    type: "Full-Time",
    description:
      "As a UI/UX Designer, you will craft engaging user experiences and work closely with our development team to implement them seamlessly.",
    skills: ["Figma", "Adobe XD", "Sketch", "User Research"],
    icon: "🎯",
  },
  {
    title: "Cloud Engineer",
    location: "Austin, USA",
    level: "Senior-level",
    experience: "4+ Years",
    type: "Full-Time",
    description:
      "We're seeking a Cloud Engineer to optimize and manage our cloud infrastructure.",
    skills: ["Azure", "AWS", "GCP", "Terraform"],
    icon: "☁️",
  },
  {
    title: "Machine Learning Engineer",
    location: "Boston, USA",
    level: "Senior-level",
    experience: "5+ Years",
    type: "Full-Time",
    description:
      "We're hiring a Machine Learning Engineer to design, develop, and optimize ML models for our cutting-edge applications.",
    skills: ["Python", "PyTorch", "TensorFlow", "Data Modeling"],
    icon: "🤖",
  },
  {
    title: "Cybersecurity Analyst",
    location: "Washington, USA",
    level: "Mid-level",
    experience: "3-5 Years",
    type: "Full-Time",
    description:
      "Join our security team as a Cybersecurity Analyst to identify and mitigate vulnerabilities and ensure the safety of our systems.",
    skills: ["Network Security", "Penetration Testing", "SIEM", "Incident Response"],
    icon: "🔒",
  },
  {
    title: "Game Developer",
    location: "Los Angeles, USA",
    level: "Mid-level",
    experience: "3-5 Years",
    type: "Full-Time",
    description:
      "We are seeking a creative Game Developer to build immersive and engaging gaming experiences using state-of-the-art technology.",
    skills: ["Unity", "C#", "Game Physics", "3D Modeling"],
    icon: "🎮",
  },
  {
    title: "AI Architect",
    location: "Remote",
    level: "Principal",
    experience: "10+ Years",
    type: "Full-Time",
    description:
      "Lead the design and implementation of enterprise-scale AI solutions. Define AI strategy, architecture patterns, and best practices across the organization.",
    skills: ["AI/ML Architecture", "LLMs", "Cloud AI", "System Design"],
    icon: "🧠",
  },
  {
    title: "AI/ML Engineer",
    location: "Remote",
    level: "Senior-level",
    experience: "8+ Years",
    type: "Full-Time",
    description:
      "Build and deploy production-grade machine learning systems. Work on cutting-edge AI projects including generative AI and large language models.",
    skills: ["Python", "TensorFlow", "PyTorch", "MLOps"],
    icon: "🤖",
  },
  {
    title: "AI Engineer",
    location: "Dallas, TX",
    level: "Mid-level",
    experience: "5+ Years",
    type: "Hybrid",
    description:
      "Develop and integrate AI-powered features into our products. Collaborate with cross-functional teams to bring innovative AI solutions to market.",
    skills: ["Python", "OpenAI", "LangChain", "Vector DBs"],
    icon: "✨",
  },
];

// Shuffle array and pick n items
const getRandomJobs = (jobs, count) => {
  const shuffled = [...jobs].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
};

const JobCard = ({ job, index, onApply }) => {
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        background: "#fff",
        borderRadius: "16px",
        border: "1px solid rgba(91, 124, 250, 0.12)",
        transition: "box-shadow 0.3s ease, border 0.3s ease",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxSizing: "border-box",
        "&:hover": {
          boxShadow: "0 12px 32px rgba(91, 124, 250, 0.15)",
          border: "1px solid rgba(91, 124, 250, 0.3)",
          "& .job-icon": {
            transform: "scale(1.05)",
          },
          "& .apply-btn": {
            background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
            color: "#fff",
          },
        },
      }}
    >
      {/* Job Type Badge */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          px: 1,
          py: 0.3,
          borderRadius: "6px",
          background:
            job.type === "Full-Time"
              ? "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)"
              : "linear-gradient(135deg, rgba(34,197,94,0.12) 0%, rgba(22,163,74,0.12) 100%)",
          fontSize: "11px",
          fontWeight: 600,
          color: job.type === "Full-Time" ? "#5b7cfa" : "#16a34a",
        }}
      >
        {job.type}
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 }}>
        <Box
          className="job-icon"
          sx={{
            width: 44,
            height: 44,
            borderRadius: "12px",
            background:
              "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            flexShrink: 0,
            transition: "transform 0.3s ease",
          }}
        >
          {job.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              color: "#764ba2",
              letterSpacing: "1px",
              mb: 0.3,
            }}
          >
            {job.id}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "15px", sm: "16px" },
              fontWeight: 700,
              color: "#1f2937",
              lineHeight: 1.3,
              pr: 5,
            }}
          >
            {job.title}
          </Typography>
        </Box>
      </Box>

      {/* Meta Info */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          <LocationOnIcon sx={{ fontSize: 14, color: "#5b7cfa" }} />
          <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
            {job.location}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          <WorkIcon sx={{ fontSize: 14, color: "#5b7cfa" }} />
          <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
            {job.level}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          <AccessTimeIcon sx={{ fontSize: 14, color: "#5b7cfa" }} />
          <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
            {job.experience}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
          <CalendarTodayIcon sx={{ fontSize: 12, color: "#5b7cfa" }} />
          <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
            {job.date}
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#4b5563",
            lineHeight: 1.6,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {job.description}
        </Typography>
      </Box>

      {/* Skills */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.8,
          mb: 1.5,
        }}
      >
        {job.skills.map((skill) => (
          <Chip
            key={skill}
            label={skill}
            size="small"
            sx={{
              background:
                "linear-gradient(135deg, rgba(91,124,250,0.08) 0%, rgba(118,75,162,0.08) 100%)",
              color: "#5b7cfa",
              fontWeight: 600,
              fontSize: "11px",
              border: "1px solid rgba(91, 124, 250, 0.15)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, rgba(91,124,250,0.15) 0%, rgba(118,75,162,0.15) 100%)",
              },
            }}
          />
        ))}
      </Box>

      {/* Apply Button */}
      <Button
        className="apply-btn"
        fullWidth
        onClick={() => onApply(job)}
        sx={{
          py: 1,
          borderRadius: "10px",
          background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)",
          color: "#5b7cfa",
          fontWeight: 700,
          fontSize: "13px",
          textTransform: "none",
          border: "1px solid rgba(91, 124, 250, 0.2)",
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(91, 124, 250, 0.25)",
          },
        }}
      >
        Apply Now
      </Button>
    </Box>
  );
};

const JobOpportunities = ({ id }) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleApply = (job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  // Randomly select 6 jobs and generate random IDs and dates on each page load
  const jobsWithIds = useMemo(() => {
    const randomJobs = getRandomJobs(jobsData, 6);
    return randomJobs.map((job) => ({
      ...job,
      id: generateJobId(),
      date: generateRandomDate(),
    }));
  }, []);

  return (
    <Box
      id={id}
      sx={{
        py: { xs: 8, sm: 10, md: 12 },
        background: "linear-gradient(135deg, #f6f8ff 0%, #eef3ff 100%)",
        scrollMarginTop: "80px",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 5, sm: 6 } }}>
          <Typography
            variant="overline"
            sx={{
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 700,
              color: "#5b7cfa",
              letterSpacing: "2px",
              mb: 1,
              display: "block",
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            OPEN POSITIONS
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "28px", sm: "36px", md: "42px" },
              fontWeight: 800,
              color: "#1b1f33",
              mb: 2,
              animation: `${fadeUp} 0.6s ease-out 0.05s both`,
            }}
          >
            Job{" "}
            <Box
              component="span"
              sx={{
                background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Opportunities
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "19px", md: "20px" },
              color: "#4c5472",
              lineHeight: 1.8,
              maxWidth: "700px",
              mx: "auto",
              animation: `${fadeUp} 0.6s ease-out 0.1s both`,
            }}
          >
            Discover new career opportunities and find the perfect role that aligns
            with your passions and skillset. Join our team and be a part of a
            community that values growth, collaboration, and innovation.
          </Typography>
        </Box>

        {/* Jobs Grid */}
        <Grid container spacing={3}>
          {jobsWithIds.map((job, idx) => (
            <Grid item xs={12} sm={6} lg={4} key={job.id}>
              <JobCard job={job} index={idx} onApply={handleApply} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Apply Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            background: "#fff",
            borderRadius: "24px",
            maxWidth: 480,
            width: "100%",
            p: { xs: 3, sm: 4 },
            position: "relative",
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.2)",
            animation: `${fadeUp} 0.4s ease-out`,
            outline: "none",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "#6b7280",
              "&:hover": {
                background: "rgba(91, 124, 250, 0.1)",
                color: "#5b7cfa",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Modal Content */}
          {selectedJob && (
            <Box sx={{ textAlign: "center" }}>
              {/* Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "20px",
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "40px",
                  mx: "auto",
                  mb: 3,
                  boxShadow: "0 12px 32px rgba(91, 124, 250, 0.3)",
                }}
              >
                {selectedJob.icon}
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: "20px", sm: "24px" },
                  fontWeight: 700,
                  color: "#1f2937",
                  mb: 1,
                }}
              >
                Apply for {selectedJob.title}
              </Typography>

              {/* Job Code */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.8,
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, rgba(91,124,250,0.12) 0%, rgba(118,75,162,0.12) 100%)",
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#5b7cfa",
                  }}
                >
                  Job Code:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#764ba2",
                    letterSpacing: "1px",
                  }}
                >
                  {selectedJob.id}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: { xs: "16px", sm: "17px" },
                  color: "#4b5563",
                  lineHeight: 1.7,
                  mb: 3,
                }}
              >
                To apply for this position, please send your resume along with the job code mentioned above to:
              </Typography>

              {/* Email Box */}
              <Box
                component="a"
                href={`mailto:hr@briozon.com?subject=Application for ${selectedJob.title} - ${selectedJob.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  py: 1.5,
                  px: 3,
                  borderRadius: "14px",
                  background: "linear-gradient(135deg, #5b7cfa 0%, #764ba2 100%)",
                  color: "#fff",
                  textDecoration: "none",
                  fontWeight: 700,
                  fontSize: { xs: "16px", sm: "18px" },
                  transition: "all 0.3s ease",
                  boxShadow: "0 8px 24px rgba(91, 124, 250, 0.3)",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 12px 32px rgba(91, 124, 250, 0.4)",
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: 22 }} />
                hr@briozon.com
              </Box>

              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#9ca3af",
                  mt: 3,
                }}
              >
                We'll get back to you within 3-5 business days
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default JobOpportunities;
